import { useEffect, useState, useCallback } from "react";
import {
    RefreshCw, ChevronLeft,
    Search, Columns3, Eye, EyeOff, Check, ChevronUp, ChevronDown,
    Loader2, Package, Activity, HardDrive, Cpu, Square, CheckSquare
} from "lucide-react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { showError, showSuccess } from "../../../utils/toast";
import baseapi from "../../../services/baseapi";
import { Pagination } from "./Pagination";

// 1. Updated Type Definitions to match new API response
interface AssetService {
    service_id: number;
    app_name: string;
    exe_name: string | null;
    cmdline: string | null;
    cgroup_path: string | null;
    pid: number;
    rx_bytes: number;
    tx_bytes: number;
    rx_packets: number;
    tx_packets: number;
    cpu_percent: number;
    memory_rss_mb: number;
    collected_at: string;
}

interface ApiResponse {
    status: boolean;
    asset_id: number;
    data: AssetService[];
    total: number; // API uses 'count' instead of 'total'
}

type ColumnKey =
    | "app_name"
    | "exe_name"
    | "pid"
    | "cpu_percent"
    | "memory_rss_mb"
    | "rx_bytes"
    | "tx_bytes"
    | "cmdline"
    | "cgroup_path"
    | "collected_at";

const AssetServices = ({ assetId }: { assetId: number }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlServiceId = searchParams.get("serviceId");
    const urlAppName = searchParams.get("appName");

    const [loading, setLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [services, setServices] = useState<AssetService[]>([]);
    const [total, setTotal] = useState(0);

    const [activeTab, setActiveTab] = useState<"monitoring" | "all">("monitoring");
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
    const [isMonitoringLoading, setIsMonitoringLoading] = useState(false);

    // Default sort updated to match new data fields
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
        key: "collected_at",
        direction: "desc",
    });



    const [serviceDashboardUrl, setServiceDashboardUrl] = useState<string | null>(null);
    const [isServiceDashboardLoading, setIsServiceDashboardLoading] = useState<boolean>(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Updated visible columns config
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        app_name: true,
        exe_name: true,
        pid: true,
        cpu_percent: true,
        memory_rss_mb: true,
        rx_bytes: false,
        tx_bytes: false,
        cmdline: false,
        cgroup_path: false,
        collected_at: true,
    });

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: "app_name", label: "App Name" },
        { key: "exe_name", label: "Exe Name" },
        { key: "pid", label: "PID" },
        { key: "cpu_percent", label: "CPU Usage" },
        { key: "memory_rss_mb", label: "Memory (MB)" },
        { key: "rx_bytes", label: "RX Data" },
        { key: "tx_bytes", label: "TX Data" },
        { key: "cmdline", label: "Cmdline" },
        { key: "cgroup_path", label: "Cgroup Path" },
        { key: "collected_at", label: "Collected At" },
    ];

    const totalPages = Math.ceil(total / itemsPerPage);

    const formatDate = (value?: string) => {
        if (!value) return "-";
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const fetchServices = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            let res;
            if (activeTab === "monitoring") {
                const params: any = {
                    asset_id: assetId,
                    skip,
                    limit: itemsPerPage,
                    sort_by: sortConfig.key,
                    sort_order: sortConfig.direction,
                };

                if (debouncedSearch) {
                    params.search = debouncedSearch;
                }

                res = await baseapi.get<ApiResponse>("/asset_network_services/latest", { params });
            } else {
                const params: any = {
                    asset_id: assetId,
                    monitored_only: false,
                    skip,
                    limit: itemsPerPage,
                    sort_by: sortConfig.key,
                    sort_order: sortConfig.direction,
                };

                if (debouncedSearch) {
                    params.search = debouncedSearch;
                }

                res = await baseapi.get<any>("/asset_network_service_monitor_config/candidates", { params });
            }

            if (res?.data?.status) {
                setServices(res.data.data || []);
                setTotal(res.data.total || res.data.count || 0);
            } else {
                setServices([]);
                setTotal(0);
            }
        } catch (e: any) {
            showError(e?.message || "Failed to fetch asset services");
            setServices([]);
            setTotal(0);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [assetId, currentPage, itemsPerPage, sortConfig, debouncedSearch, activeTab]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);



    useEffect(() => {
        if (urlServiceId) {
            const fetchServiceSpecificDashboard = async () => {
                setIsServiceDashboardLoading(true);
                try {
                    const pageRes = await baseapi.get("/dashboards/pages/asset_network_services");
                    if (pageRes.data?.status && pageRes.data?.dashboards?.length > 0) {
                        const dashboardName = pageRes.data.dashboards[0].name;
                        const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
                        const clientId = info.client_id;

                        const params: any = {
                            dashboard: dashboardName,
                            client_id: clientId,
                            asset_id: assetId,
                            filters: JSON.stringify({ service_id: Number(urlServiceId) })
                        };

                        const response = await baseapi.get("/dashboards/embed-url", { params });
                        if (response.data.status) {
                            let url = response.data.iframe_url || response.data.url || response.data.embed_url;
                            if (url) {
                                if (url.startsWith('/')) {
                                    url = window.location.origin + url;
                                }
                                setServiceDashboardUrl(url);
                            }
                        } else {
                            showError(response.data.message || "Failed to load service dashboard embed URL");
                        }
                    } else {
                        showError("Dashboard configuration not found.");
                    }
                } catch (error: any) {
                    showError(error.message || "Failed to fetch service dashboard URL");
                } finally {
                    setIsServiceDashboardLoading(false);
                }
            };
            fetchServiceSpecificDashboard();
        } else {
            setServiceDashboardUrl(null);
        }
    }, [urlServiceId, assetId]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleRefresh = () => {
        setSearchTerm("");
        setDebouncedSearch("");
        setCurrentPage(1);
        setSelectedServiceIds([]);
        setIsRefreshing(true);
        fetchServices();
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
        setCurrentPage(1);
    };

    const getSortIcon = (key: string) => {
        if (sortConfig.key !== key) return <ChevronDown size={10} className="opacity-20" />;
        return sortConfig.direction === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />;
    };

    const handleTabChange = (tab: "monitoring" | "all") => {
        setActiveTab(tab);
        setCurrentPage(1);
        setSelectedServiceIds([]);
        setServices([]);
        if (tab === "all") {
            setSortConfig({ key: "app_name", direction: "asc" });
        } else {
            setSortConfig({ key: "collected_at", direction: "desc" });
        }
    };

    const handleMonitorSelect = async () => {
        if (selectedServiceIds.length === 0) return;
        setIsMonitoringLoading(true);
        try {
            const payload = {
                asset_id: assetId,
                service_ids: selectedServiceIds,
            };
            const response = await baseapi.post("/asset_network_service_monitor_config/select", payload);
            if (response.data.status) {
                showSuccess("Successfully updated monitor configuration");
                setSelectedServiceIds([]);
                fetchServices();
            } else {
                showError(response.data.message || "Failed to update monitor configuration");
            }
        } catch (error: any) {
            showError(error.message || "Failed to monitor services");
        } finally {
            setIsMonitoringLoading(false);
        }
    };

    const handleMonitorUnselect = async () => {
        if (selectedServiceIds.length === 0) return;
        setIsMonitoringLoading(true);
        try {
            const payload = {
                asset_id: assetId,
                service_ids: selectedServiceIds,
            };
            const response = await baseapi.post("/asset_network_service_monitor_config/unselect", payload);
            if (response.data.status) {
                showSuccess("Successfully unmonitored services");
                setSelectedServiceIds([]);
                fetchServices();
            } else {
                showError(response.data.message || "Failed to unmonitor services");
            }
        } catch (error: any) {
            showError(error.message || "Failed to unmonitor services");
        } finally {
            setIsMonitoringLoading(false);
        }
    };

    const openGraph = (service: AssetService) => {
        // Mutate the existing searchParams to preserve other keys like 'tab'
        searchParams.set("serviceId", service.service_id.toString());
        if (service.app_name) {
            searchParams.set("appName", service.app_name);
        }
        setSearchParams(searchParams);
    };

    const closeGraph = () => {
        // Only delete 'serviceId' to preserve 'tab'
        searchParams.delete("serviceId");
        searchParams.delete("appName");
        setSearchParams(searchParams);
    };

    if (urlServiceId) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="p-3 pb-20 bg-gray-900 w-full min-h-[calc(100vh-100px)] rounded-xl flex flex-col"
            >
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={closeGraph}
                        className="px-4 py-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm font-bold tracking-wider"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Services
                    </button>
                    <h2 className="text-gray-200 font-bold uppercase tracking-wider">
                        {urlAppName ? `${urlAppName}` : "Service Dashboard"}
                    </h2>
                </div>

                <div className="flex-1 min-h-[700px] w-full bg-gray-900 rounded-md border border-gray-700 relative flex items-center justify-center overflow-hidden">
                    {isServiceDashboardLoading ? (
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider">Loading dashboard...</span>
                        </div>
                    ) : serviceDashboardUrl ? (
                        <iframe src={serviceDashboardUrl} className="w-full h-full border-none absolute inset-0 bg-transparent" title="Service Dashboard" />
                    ) : (
                        <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Failed to load dashboard</div>
                    )}
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="p-3 pb-20 bg-gray-900 w-full min-h-[calc(100vh-100px)] rounded-xl flex flex-col"
        >
            <div className="w-full mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-700 pb-2">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Activity className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-white leading-tight">Monitorable Services</h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {activeTab === "monitoring" && selectedServiceIds.length > 0 && (
                            <button
                                onClick={handleMonitorUnselect}
                                disabled={isMonitoringLoading}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-red-900/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                                {isMonitoringLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <Check className="w-3 h-3" />
                                )}
                                Unmonitor ({selectedServiceIds.length})
                            </button>
                        )}

                        {activeTab === "all" && selectedServiceIds.length > 0 && (
                            <button
                                onClick={handleMonitorSelect}
                                disabled={isMonitoringLoading}
                                className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-orange-900/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                                {isMonitoringLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <Check className="w-3 h-3" />
                                )}
                                Monitor ({selectedServiceIds.length})
                            </button>
                        )}

                        <button
                            onClick={handleRefresh}
                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin text-orange-500" : ""}`} />
                        </button>

                        <div className="relative w-64">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search services..."
                                className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {activeTab === "monitoring" && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowColumnFilter(!showColumnFilter)}
                                    className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2"
                                >
                                    <Columns3 className="w-3.5 h-3.5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                                </button>
                                {showColumnFilter && (
                                    <div className="absolute top-full mt-2 right-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5">
                                        <div className="px-3 py-1.5 text-[10px] font-bold text-gray-200 uppercase border-b border-gray-800 mb-1">
                                            Toggle Columns
                                        </div>
                                        {columnOptions.map((option) => (
                                            <button
                                                key={option.key}
                                                onClick={() =>
                                                    setVisibleColumns((prev) => ({ ...prev, [option.key]: !prev[option.key] }))
                                                }
                                                className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {visibleColumns[option.key] ? (
                                                        <Eye className="w-3 h-3 text-orange-500" />
                                                    ) : (
                                                        <EyeOff className="w-3 h-3 text-gray-600" />
                                                    )}
                                                    {option.label}
                                                </div>
                                                <div className={`w-3 h-3 rounded border flex items-center justify-center ${visibleColumns[option.key] ? "bg-orange-600 border-orange-600" : "border-gray-700"}`}>
                                                    {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-3 border-b border-gray-800">
                    <button
                        onClick={() => handleTabChange("monitoring")}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all duration-200 flex items-center gap-1.5 ${activeTab === "monitoring"
                                ? "text-orange-500 border-orange-500 bg-gray-800/40"
                                : "text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/20"
                            }`}
                    >
                        Monitoring
                        {activeTab === "monitoring" && !loading && (
                            <span className="text-orange-400 font-mono">({total})</span>
                        )}
                    </button>
                    <button
                        onClick={() => handleTabChange("all")}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all duration-200 flex items-center gap-1.5 ${activeTab === "all"
                                ? "text-orange-500 border-orange-500 bg-gray-800/40"
                                : "text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/20"
                            }`}
                    >
                        Unmonitored
                        {activeTab === "all" && !loading && (
                            <span className="text-orange-400 font-mono">({total})</span>
                        )}
                    </button>
                </div>

                <div className="bg-gray-800/40 border border-gray-700 rounded-t-xl overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[10px] border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                {activeTab === "monitoring" ? (
                                    <tr className="border-b border-gray-700/50">
                                        <th className="px-3 py-2 w-10 text-[10px] font-bold text-gray-200 uppercase tracking-widest text-center">
                                            <div
                                                className="cursor-pointer group flex items-center justify-center"
                                                onClick={() => {
                                                    const allChecked = services.length > 0 && services.every(s => selectedServiceIds.includes(s.service_id));
                                                    if (allChecked) {
                                                        const visibleIds = services.map(s => s.service_id);
                                                        setSelectedServiceIds(prev => prev.filter(id => !visibleIds.includes(id)));
                                                    } else {
                                                        const allIds = services.map(s => s.service_id);
                                                        setSelectedServiceIds(prev => Array.from(new Set([...prev, ...allIds])));
                                                    }
                                                }}
                                            >
                                                {services.length > 0 && services.every(s => selectedServiceIds.includes(s.service_id)) ? (
                                                    <CheckSquare size={16} className="text-orange-500 fill-orange-500/10" />
                                                ) : (
                                                    <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                )}
                                            </div>
                                        </th>
                                        {columnOptions.map(({ key, label }) =>
                                            visibleColumns[key] ? (
                                                <th
                                                    key={key}
                                                    onClick={() => handleSort(key)}
                                                    className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-100 transition-colors"
                                                >
                                                    <div className="flex items-center gap-1">
                                                        {label}
                                                        {getSortIcon(key)}
                                                    </div>
                                                </th>
                                            ) : null
                                        )}
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest">
                                            Action
                                        </th>
                                    </tr>
                                ) : (
                                    <tr className="border-b border-gray-700/50">
                                        <th className="px-3 py-2 w-10 text-[10px] font-bold text-gray-200 uppercase tracking-widest text-center">
                                            <div
                                                className="cursor-pointer group flex items-center justify-center"
                                                onClick={() => {
                                                    const allChecked = services.length > 0 && services.every(s => selectedServiceIds.includes(s.service_id));
                                                    if (allChecked) {
                                                        const visibleIds = services.map(s => s.service_id);
                                                        setSelectedServiceIds(prev => prev.filter(id => !visibleIds.includes(id)));
                                                    } else {
                                                        const allIds = services.map(s => s.service_id);
                                                        setSelectedServiceIds(prev => Array.from(new Set([...prev, ...allIds])));
                                                    }
                                                }}
                                            >
                                                {services.length > 0 && services.every(s => selectedServiceIds.includes(s.service_id)) ? (
                                                    <CheckSquare size={16} className="text-orange-500 fill-orange-500/10" />
                                                ) : (
                                                    <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleSort("app_name")}
                                            className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-1">
                                                App Name
                                                {getSortIcon("app_name")}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleSort("exe_name")}
                                            className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-1">
                                                Exe Name
                                                {getSortIcon("exe_name")}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleSort("cgroup_path")}
                                            className="px-3 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-1">
                                                Cgroup Path
                                                {getSortIcon("cgroup_path")}
                                            </div>
                                        </th>
                                    </tr>
                                )}
                            </thead>

                            <tbody className="divide-y divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan={activeTab === "monitoring" ? Object.values(visibleColumns).filter(Boolean).length + 2 : 4} className="py-20 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200 italic">
                                                    Loading services...
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : services.length === 0 ? (
                                    <tr>
                                        <td colSpan={activeTab === "monitoring" ? Object.values(visibleColumns).filter(Boolean).length + 2 : 4} className="py-20 text-center text-gray-200 font-bold uppercase tracking-widest italic">
                                            No services found
                                        </td>
                                    </tr>
                                ) : (
                                    services.map((service, idx) => (
                                        activeTab === "monitoring" ? (
                                            <tr key={`${service.service_id}-${idx}`} className="hover:bg-gray-800/60 transition-colors group">
                                                {/* Checkbox */}
                                                <td className="px-3 py-1.5 w-10 text-center">
                                                    <div
                                                        className="cursor-pointer group flex items-center justify-center"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const isSelected = selectedServiceIds.includes(service.service_id);
                                                            if (isSelected) {
                                                                setSelectedServiceIds(prev => prev.filter(id => id !== service.service_id));
                                                            } else {
                                                                setSelectedServiceIds(prev => [...prev, service.service_id]);
                                                            }
                                                        }}
                                                    >
                                                        {selectedServiceIds.includes(service.service_id) ? (
                                                            <CheckSquare size={16} className="text-orange-500 fill-orange-500/10" />
                                                        ) : (
                                                            <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* App Name */}
                                                {visibleColumns.app_name && (
                                                    <td className="px-3 py-1.5 font-medium text-gray-200">
                                                        <button
                                                            onClick={() => openGraph(service)}
                                                            className="flex items-center gap-2 text-left hover:text-orange-400 transition-colors"
                                                        >
                                                            <Package size={12} className="text-orange-400 flex-shrink-0" />
                                                            <span className="truncate max-w-[220px]" title={service.app_name}>
                                                                {service.app_name || "—"}
                                                            </span>
                                                        </button>
                                                    </td>
                                                )}

                                                {/* Exe Name */}
                                                {visibleColumns.exe_name && (
                                                    <td className="px-3 py-1.5 font-mono text-[10px] text-gray-200">
                                                        <span className="bg-gray-900 px-2 py-0.5 rounded border border-gray-800">
                                                            {service.exe_name || "Unknown"}
                                                        </span>
                                                    </td>
                                                )}

                                                {/* PID */}
                                                {visibleColumns.pid && (
                                                    <td className="px-3 py-1.5">
                                                        <span className="bg-blue-500/10 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded text-[10px] font-bold">
                                                            {service.pid}
                                                        </span>
                                                    </td>
                                                )}

                                                {/* CPU Usage */}
                                                {visibleColumns.cpu_percent && (
                                                    <td className="px-3 py-1.5 text-gray-200 font-mono text-[10px]">
                                                        <div className="flex items-center gap-1.5 text-rose-400">
                                                            <Cpu size={10} />
                                                            {service.cpu_percent?.toFixed(2) || "0.00"}%
                                                        </div>
                                                    </td>
                                                )}

                                                {/* Memory Usage */}
                                                {visibleColumns.memory_rss_mb && (
                                                    <td className="px-3 py-1.5 text-gray-200 font-mono text-[10px]">
                                                        <div className="flex items-center gap-1.5 text-emerald-400">
                                                            <HardDrive size={10} />
                                                            {service.memory_rss_mb?.toFixed(2) || "0.00"} MB
                                                        </div>
                                                    </td>
                                                )}

                                                {/* RX Bytes */}
                                                {visibleColumns.rx_bytes && (
                                                    <td className="px-3 py-1.5 text-gray-200 font-mono text-[10px]">
                                                        {formatBytes(service.rx_bytes)}
                                                    </td>
                                                )}

                                                {/* TX Bytes */}
                                                {visibleColumns.tx_bytes && (
                                                    <td className="px-3 py-1.5 text-gray-200 font-mono text-[10px]">
                                                        {formatBytes(service.tx_bytes)}
                                                    </td>
                                                )}

                                                {/* Cmdline */}
                                                {visibleColumns.cmdline && (
                                                    <td className="px-3 py-1.5 max-w-[240px]">
                                                        <div className="text-[10px] text-gray-400 truncate" title={service.cmdline || ""}>
                                                            {service.cmdline || "-"}
                                                        </div>
                                                    </td>
                                                )}

                                                {/* Cgroup Path */}
                                                {visibleColumns.cgroup_path && (
                                                    <td className="px-3 py-1.5 max-w-[200px]">
                                                        <div className="text-[10px] text-gray-400 truncate" title={service.cgroup_path || ""}>
                                                            {service.cgroup_path || "-"}
                                                        </div>
                                                    </td>
                                                )}

                                                {/* Collected At */}
                                                {visibleColumns.collected_at && (
                                                    <td className="px-3 py-1.5 text-gray-300 font-mono text-[10px]">
                                                        {formatDate(service.collected_at)}
                                                    </td>
                                                )}

                                                {/* Action View Graph */}
                                                <td className="px-3 py-1.5">
                                                    <button
                                                        onClick={() => openGraph(service)}
                                                        className="px-2 py-1 rounded bg-orange-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-bold uppercase hover:bg-orange-500/20"
                                                    >
                                                        View Stats
                                                    </button>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={`${service.service_id}-${idx}`} className="hover:bg-gray-800/40 transition-colors duration-100">
                                                {/* Checkbox */}
                                                <td className="px-3 py-2 w-10 text-center">
                                                    <div
                                                        className="cursor-pointer group flex items-center justify-center"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const isSelected = selectedServiceIds.includes(service.service_id);
                                                            if (isSelected) {
                                                                setSelectedServiceIds(prev => prev.filter(id => id !== service.service_id));
                                                            } else {
                                                                setSelectedServiceIds(prev => [...prev, service.service_id]);
                                                            }
                                                        }}
                                                    >
                                                        {selectedServiceIds.includes(service.service_id) ? (
                                                            <CheckSquare size={16} className="text-orange-500 fill-orange-500/10" />
                                                        ) : (
                                                            <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* App Name */}
                                                <td className="px-3 py-2 font-medium text-gray-200">
                                                    <div className="flex items-center gap-2 text-left">
                                                        <Package size={12} className="text-orange-400 flex-shrink-0" />
                                                        <span className="truncate max-w-[220px] text-[11px] font-semibold text-white" title={service.app_name}>
                                                            {service.app_name || "—"}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Exe Name */}
                                                <td className="px-3 py-2 font-mono text-[10px] text-gray-200">
                                                    <span className="bg-gray-900 px-2 py-0.5 rounded border border-gray-800">
                                                        {service.exe_name || "Unknown"}
                                                    </span>
                                                </td>

                                                {/* Cgroup Path */}
                                                <td className="px-3 py-2 max-w-[200px]">
                                                    <div className="text-[10px] text-gray-400 truncate" title={service.cgroup_path || ""}>
                                                        {service.cgroup_path || "-"}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="rounded-b-xl overflow-hidden border border-t-0 border-gray-700 mt-0">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={total}
                        onItemsPerPageChange={(val: number) => {
                            setItemsPerPage(val);
                            setCurrentPage(1);
                        }}
                        itemsPerPageOptions={[5, 10, 25, 35, 50, 100]}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default AssetServices;